<template>
  <div class="review_grid">
    <UserCard class="review_user !py-0" v-if="user" :user :badges plain>
      <template #postDisplayName>
        <span
          v-if="highlightUser"
          class="text-xs uppercase font-bold text-white bg-nami-comi-blue px-1.5 rounded-full"
        >
          {{ "creator" }}
        </span>
      </template>
      <template #postUsername>
        <span class="text-neutral-500 mx-1">•</span>
        <span class="text-neutral-500 whitespace-nowrap text-sm">
          {{ creationTimeAgo }}
        </span>
      </template>
    </UserCard>
    <div class="review_options">
      <NamiButton
        v-if="showEdit"
        small
        text
        :icon="IconPencil"
        button-type="secondary"
        @buttonClick="$emit('edit')"
      />
      <NamiButton
        v-if="showDelete"
        small
        text
        :icon="IconTrash"
        button-type="danger"
        @buttonClick="$emit('delete')"
      />
    </div>
    <ReviewDisplay class="review_rating" :rating="rating.attributes.rating" />
    <div v-if="reviewMessage" class="review_message">{{ reviewMessage }}</div>
  </div>
</template>

<script setup lang="ts">
import { isBadge, isExpanded, isUser, type RatingEntity } from "~/src/api";
import { IconPencil, IconTrash } from "@tabler/icons-vue";
import { parseUserForDisplay } from "~/utils/display/user";

const nuxtApp = useNuxtApp();
const { locale } = useI18n();

const { rating } = defineProps<{
  rating: RatingEntity;
  highlightUser?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
}>();

defineEmits<{
  (e: "edit"): void;
  (e: "delete"): void;
}>();

const reviewMessage = computed(() =>
  extractFromLocale(locale.value, rating.attributes.message),
);

const user = computed(() => rating.relationships.find(isUser));

const { username, displayName, official, verified } = parseUserForDisplay(
  user,
  "small",
);
const badges = computed(() =>
  rating.relationships.filter(isBadge).filter(isExpanded),
);

const timeAgo = useTimeAgo(locale);
const creationTimeAgo = timeAgo.value(
  new Date(rating.attributes.createdAt),
  "mini",
);
</script>

<style scoped lang="postcss">
.review_grid {
  @apply grid gap-1;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "user options"
    "user options"
    "stars stars"
    "review review";
}

.review_user {
  grid-area: user;
}

.review_options {
  grid-area: options;
  @apply flex items-center justify-center mb-auto;
}

.review_rating {
  grid-area: stars;
  @apply ml-14;
}

.review_message {
  grid-area: review;
  @apply ml-14;
}
</style>
